import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import BestGradientPicker from "react-best-gradient-color-picker";
import * as React from "react";
import { userSlice } from "@/redux/User";
import { useDispatch } from "react-redux";

// Generate a unique ID for each picker instance
let instanceCounter = 0;
const generateUniqueId = () => `gradient-picker-${instanceCounter++}`;

// Use a global event to manage picker state across all instances
const PICKER_OPEN_EVENT = "gradient-picker-opened";

// Type definition for the callback function
type CloseCallbackType = (id: string) => void;

// Improved registry with event-based communication
const pickerRegistry = {
  openPickerId: null as string | null,
  register: function (id: string, isOpen: boolean) {
    if (isOpen) {
      // Broadcast an event when a picker is opened
      const event = new CustomEvent(PICKER_OPEN_EVENT, { detail: { id } });
      window.dispatchEvent(event);
      this.openPickerId = id;
    } else if (this.openPickerId === id) {
      this.openPickerId = null;
    }
  },
  closeCallback: null as CloseCallbackType | null,
  setCloseCallback: function (callback: CloseCallbackType) {
    this.closeCallback = callback;
  },
};

// Default initial gradient if none provided
const DEFAULT_GRADIENT =
  "linear-gradient(90deg, rgb(251, 95, 103) 0%, rgb(126, 32, 207) 100%)";

const isGradient = (color: string) => {
  if (!color) return false;
  return (
    color.includes("linear-gradient") ||
    color.includes("radial-gradient") ||
    color.includes("conic-gradient") ||
    color.includes("repeating-")
  );
};

const isSimpleGradient = (color: string) => {
  if (!color) return false;
  return color.startsWith("linear-gradient(") && !color.includes("repeating");
};

const isComplexGradient = (color: string) => {
  if (!color) return false;
  return (
    color.includes("repeating-") ||
    color.includes("conic-gradient") ||
    (color.includes(",") && color.includes("repeating-"))
  );
};

const colorToGradient = (color: string) => {
  if (!color) return DEFAULT_GRADIENT;
  if (isGradient(color)) return color;
  return `linear-gradient(90deg, ${color} 0%, ${color} 100%)`;
};

const extractFirstColor = (gradient: string) => {
  if (!gradient) return "";
  const firstColorMatch = gradient.match(
    /rgb\([^)]+\)|#[0-9a-fA-F]{3,6}|\b[a-zA-Z]+\b/,
  );
  return firstColorMatch ? firstColorMatch[0] : gradient;
};
const simplifyGradientForPicker = (gradient: string) => {
  if (!gradient || isSimpleGradient(gradient)) {
    return gradient;
  }

  const colorMatches = gradient.match(
    /(rgb(a)?\([^)]+\)|#[0-9a-fA-F]{3,6}|\b[a-zA-Z]+\b)/g,
  );

  if (!colorMatches) return DEFAULT_GRADIENT;

  const ignoreWords = [
    "linear",
    "radial",
    "repeating",
    "conic",
    "gradient",
    "deg",
    "to",
    "top",
    "bottom",
    "left",
    "right",
    "circle",
  ];

  const filteredColors = colorMatches.filter(
    (color) => !ignoreWords.includes(color.toLowerCase()),
  );

  if (filteredColors.length >= 2) {
    return `linear-gradient(90deg, ${filteredColors[0]} 0%, ${filteredColors[1]} 100%)`;
  } else if (filteredColors.length === 1) {
    return `linear-gradient(90deg, ${filteredColors[0]} 0%, ${filteredColors[0]} 100%)`;
  }

  return DEFAULT_GRADIENT;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

class GradientPickerErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: (err: Error) => void },
  { hasError: boolean }
> {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.props.onError(error);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export const GradientPicker2 = ({
  onGradientChange,
  initialColor = DEFAULT_GRADIENT,
  usePortal = false,
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(initialColor);
  const [isComplex, setIsComplex] = useState(false);
  const [position, setPosition] = useState({
    openUpwards: false,
    openToLeft: false,
  });
  const pickerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const instanceIdRef = useRef(generateUniqueId());
  const isInitialMount = useRef(true);
  const wasOpenRef = useRef(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (initialColor && !isInitialMount.current) {
      setValue(initialColor);
      setIsComplex(isComplexGradient(initialColor));
    }
    isInitialMount.current = false;
  }, [initialColor]);

  useEffect(() => {
    setIsComplex(isComplexGradient(value));
  }, [value]);

  useEffect(() => {
    const handlePickerOpen = (event: CustomEvent) => {
      const { id } = event.detail;
      if (id !== instanceIdRef.current && open) {
        setOpen(false);
      }
    };
    window.addEventListener(
      PICKER_OPEN_EVENT,
      handlePickerOpen as EventListener,
      true,
    );
    return () => {
      window.removeEventListener(
        PICKER_OPEN_EVENT,
        handlePickerOpen as EventListener,
        true,
      );
    };
  }, [open]);

  const handleValueChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      if (onGradientChange) {
        onGradientChange(newValue);
      }
    },
    [onGradientChange],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        pickerRef.current &&
        triggerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const renderColorPreview = () => {
    return (
      <div
        ref={triggerRef}
        className="color-preview"
        onClick={() => handleSetOpen(!open)}
        style={{
          background: value,
          width: "100%",
          height: "20px",
          borderRadius: "4px",
          cursor: "pointer",
          border: "1px solid #ddd",
        }}
      />
    );
  };

  // ─── Portal positioning ──────────────────────────────────────────────────────
  // Imperative DOM mutation via useLayoutEffect: fires synchronously after React
  // commits the portal to the DOM, before the browser paints. This means the
  // element is NEVER visible at a wrong position — no React state updates fire
  // so nothing around it re-renders either.
  useLayoutEffect(() => {
    if (!open || !usePortal) {
      wasOpenRef.current = false;
      return;
    }
    const el = pickerRef.current;
    const trigger = triggerRef.current;
    if (!el || !trigger) return;

    const isFirstOpen = !wasOpenRef.current;
    wasOpenRef.current = true;

    if (isFirstOpen) {
      // Immediately invisible — position it silently before any paint
      el.style.opacity = "0";
      el.style.visibility = "hidden";
      el.style.pointerEvents = "none";
      el.style.transition = "none";
    }

    const triggerRect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const isGradientColor = isGradient(value);
    const complex = isComplexGradient(value);
    let pickerHeight = isGradientColor ? 445 : 425;
    if (complex) pickerHeight += 65;
    const pickerWidth = 252;
    const popupPadding = 50;
    const popupOuterWidth = pickerWidth + popupPadding * 2;
    const gutter = 6;

    // Use viewport space — fixed positioning so container overflow never clips
    const availableBelow = viewportHeight - triggerRect.bottom;
    const availableAbove = triggerRect.top;

    // Open upward if there isn't enough room below
    // (even if above is tight, it's better than being off-screen)
    const openUpwards = availableBelow < pickerHeight;
    const openToLeft = triggerRect.left + pickerWidth > viewportWidth;

    // Raw viewport-coordinate top (fixed positioning, no scroll offset)
    let top: number;
    if (openUpwards) {
      top = triggerRect.top - pickerHeight - gutter;
    } else {
      top = triggerRect.bottom + gutter;
    }
    // Subtract half the outer padding so the shadow/border doesn't add gap
    top -= popupPadding / 2;

    const rawLeft =
      (openToLeft ? triggerRect.right - pickerWidth : triggerRect.left) -
      popupPadding;
    const minLeft = gutter;
    const maxLeft = Math.max(minLeft, viewportWidth - popupOuterWidth - gutter);

    // Clamp within viewport bounds
    top = clamp(
      top,
      gutter,
      viewportHeight - pickerHeight - popupPadding - gutter,
    );
    const left = clamp(rawLeft, minLeft, maxLeft);

    // Apply correct position — still invisible, no paint has happened yet
    el.style.position = "fixed";
    el.style.zIndex = "9999";
    el.style.padding = "25px";
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;

    if (isFirstOpen) {
      // Reveal on the very next animation frame — position is already locked in
      requestAnimationFrame(() => {
        el.style.transition = "opacity 0.15s ease-out";
        el.style.visibility = "visible";
        el.style.opacity = "1";
        el.style.pointerEvents = "auto";
      });
    }
  }, [open, value, usePortal]);

  // Non-portal path: keep open-direction state for the inline CSS below
  useEffect(() => {
    if (!open || usePortal) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setPosition({
      openUpwards:
        window.innerHeight - rect.bottom < 425 &&
        rect.top > window.innerHeight - rect.bottom,
      openToLeft: rect.left + 252 > window.innerWidth,
    });
  }, [open, value, usePortal]);

  const handleSetOpen = useCallback((isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) pickerRegistry.register(instanceIdRef.current, true);
  }, []);

  const renderPickerPortal = () => {
    if (!open) return null;

    const innerStyle = {
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
      background: "#fff",
      borderRadius: "8px",
      padding: "16px",
      border: "1px solid rgba(0, 0, 0, 0.1)",
      minWidth: "252px",
      width: "252px",
    };

    const pickerContent = (
      <div
        ref={pickerRef}
        className="gradient-picker-popup"
        style={{
          position: "absolute",
          visibility: "hidden",
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        <div style={usePortal ? innerStyle : {}}>
          {isComplex && (
            <div
              style={{
                background: "#fff3cd",
                color: "#856404",
                padding: "8px",
                borderRadius: "4px",
                marginBottom: "12px",
                fontSize: "12px",
                border: "1px solid #ffeaa7",
              }}
            >
              ⚠️ Complex gradient detected. Picker shows <br /> simplified
              version. Use CSS input above for <br /> full control.
            </div>
          )}

          <GradientPickerErrorBoundary
            onError={(error) =>
              dispatch(
                userSlice.actions.setToastPopUp({
                  show: true,
                  toastType: "error",
                  message: `Invalid input detected: ${error.message}`,
                }),
              )
            }
          >
            <>
              <style>{`
              .gradient-picker-popup input[id^="rbgcp-"] {
                font-size: 11px !important;
                height: 24px !important;
                padding: 2px 4px !important;
                border-radius: 4px !important;
              }
              .gradient-picker-popup div[id^="rbgcp-inputs-wrap"] {
                padding-top: 10px !important;
                column-gap: 4px !important;
              }
              .gradient-picker-popup div[id^="rbgcp-hex-input-wrapper"] {
                width: 64px !important;
              }
              .gradient-picker-popup div[id^="rbgcp-"][id$="-input-wrapper"] {
                flex-shrink: 1 !important;
              }
            `}</style>
              <BestGradientPicker
                value={isComplex ? simplifyGradientForPicker(value) : value}
                onChange={handleValueChange}
                width={220}
                height={250}
                disableDarkMode
                hideColorGuide
                hideAdvancedSliders
                hidePresets
                hideEyeDrop
              />
            </>
          </GradientPickerErrorBoundary>
        </div>
      </div>
    );

    if (usePortal) {
      // Portal to body with fixed positioning — never clipped by any ancestor overflow
      return createPortal(pickerContent, document.body);
    }

    // Original inline behaviour (builder) — position relative to the container
    return (
      <div
        ref={pickerRef}
        className="gradient-picker-popup"
        style={{
          position: "absolute",
          top: position.openUpwards ? "auto" : "calc(100% + 13px)",
          bottom: position.openUpwards ? "calc(100% + 13px)" : "auto",
          left: position.openToLeft ? "auto" : "-20px",
          right: position.openToLeft ? 0 : "auto",
          zIndex: 1000,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          background: "#fff",
          borderRadius: "8px",
          padding: "16px",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          minWidth: "252px",
          width: "252px",
          transition: "opacity 0.2s ease-out, transform 0.2s ease-out",
          opacity: 1,
          transform: "translateY(0)",
        }}
      >
        {pickerContent.props.children}
      </div>
    );
  };

  return (
    <div className="gradient-picker-container" style={{ position: "relative" }}>
      {renderColorPreview()}
      {renderPickerPortal()}
    </div>
  );
};

export default GradientPicker2;
